import React, { Fragment, useCallback, useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Card,
  CardContent,
  Typography,
  Alert,
  Skeleton
} from '@mui/material';
import ErrorDetailsModal from './ErrorDetailsModal';
import { flatMap, snakeCase, startCase } from 'lodash';

export type NameAndTitle = {
  name: string,
  title: string,
}

interface ImportPreviewProps {
  loading?: boolean;
  validRecordCount?: number;
  totalErrorCount?: number;
  exampleColumnNames: string[];
  errorColumnNames: string[],
  exampleColumnNamesAndTitles: NameAndTitle[];
  errorColumnNamesAndTitles: NameAndTitle[];
  errors?: {
    errors: { // @TODO: this is repeated on parent screen consolidate;
      instancePath: string;
      keyword: string;
      message: string;
    }[];
    csvRow: string[];
    rowIndex: number;
  }[];
  exampleRecords: {
    record: string[];
    rowIndex: number;
  }[];
  onTriggerImportProcess: () => void;
  onTriggerDeleteDataImport: () => void;
}

const ImportPreview: React.FC<ImportPreviewProps> = ({ loading, errors, validRecordCount = 0, totalErrorCount = 0, exampleColumnNames = [], errorColumnNames = [], exampleColumnNamesAndTitles, errorColumnNamesAndTitles, exampleRecords, onTriggerImportProcess, onTriggerDeleteDataImport }) => {
  const [selectedRowErrors, setSelectedRowErrors] = useState<string[] | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedRowErrors(null);
  };

  const getColumnIndexes = useCallback((columnNames: string[], columnNamesAndTitles: NameAndTitle[]): Record<string, number> => {
    return columnNamesAndTitles.reduce((acc, nameTitle) => {
      const snakeCaseLookup = columnNames.indexOf(snakeCase(nameTitle.name));
      const index = snakeCaseLookup !== -1 ? snakeCaseLookup : columnNames.indexOf(nameTitle.name);

      if (index !== -1) {
        acc[nameTitle.name] = index;
      }
      return acc;
    }, {} as Record<string, number>)
  }, []);

  const exampleColumnIndexes = getColumnIndexes(exampleColumnNames, exampleColumnNamesAndTitles);
  const errorColumnIndexes = getColumnIndexes(errorColumnNames, errorColumnNamesAndTitles);

  const formatErrorMessage = (error: {
    message: string;
    instancePath: string;
    keyword: string;
  }) => {
    if (error.message) {
      if(error.instancePath) {
        const path = error.instancePath[0] == "/" ? error.instancePath.substring(1) : error.instancePath;
        return `${startCase(path)} ${error.message}`;
      }
      return error.message;
    } 
    return "Unknown error";
  };


  const validationErrors = errors?.filter((error) => !!error.csvRow) || [];

  const generalErrors = errors?.filter((error) => !error.csvRow) || [];

  const importButton = (
    !loading && validRecordCount > 0 ? (
      <Box display="flex" justifyContent="right" alignItems="center" mt={2}>
        <Box>
          <Button
            onClick={() => onTriggerImportProcess()}
            variant="contained"
            data-testid="import-button"
            color="primary"
            sx={{ mr: 2 }}>
            Import {validRecordCount} of {validRecordCount + totalErrorCount} records
          </Button>
          <Button color="error" onClick={onTriggerDeleteDataImport}>Cancel</Button>
        </Box>
      </Box>
    ) : null
  );

  return (
    <>
    <Typography fontWeight={500} component="span">
      Import Preview
    </Typography>
    <Card>
      <CardContent>
        {loading && (
          <Fragment data-testid="patient-preview-skeleton-loading">
            <Skeleton variant="text" sx={{ fontSize: "1.2rem" }} width={240} />
            <Skeleton
              variant="text"
              sx={{ fontSize: "0.5em", mb: 0.5 }}
            />
            <Skeleton
              variant="text"
              sx={{ fontSize: "0.5em" }}
              width={420}
            />
          </Fragment>
        )}
        {!loading && totalErrorCount && totalErrorCount > (errors?.length || 0) ? (
          <Alert severity="warning">
            A total of {totalErrorCount} errors were detected in the file. The first {errors?.length} are displayed below for your review.
          </Alert>
        ) : null}
        {!loading && totalErrorCount === 0 && generalErrors?.length === 0 && validRecordCount > 0 ? (
          <Alert severity="success">
            No errors found.
          </Alert>
        ) : null}
        <Box mt={2} mb={2}>
          {!loading && generalErrors?.length ? (
            flatMap(generalErrors, (errorGroup) =>
              errorGroup?.errors?.map((error) => (
                <Alert style={{ marginBottom: 2 }} severity="error" key={error.message}>
                  {error.message}
                </Alert>
              ))
            )
          ) : null}
          {!loading && validationErrors?.length == 0 && exampleRecords?.length ? (
            (
              <TableContainer component={Box} sx={{ boxShadow: 1, borderRadius: 1 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Row</TableCell>
                      {exampleColumnNamesAndTitles?.map((nameTitle) => {
                        return (
                          <TableCell key={nameTitle.name}>{nameTitle.title}</TableCell>    
                        )
                      })}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {exampleRecords?.map((row, index) => {
                      return (
                        <>
                          {index > 0 && exampleRecords[index-1].rowIndex != row.rowIndex-1 ? (
                            <TableRow key={`${index}.break`}>
                              <TableCell sx={{pt: 0, pb: 1}}>...</TableCell>
                              {exampleColumnNamesAndTitles?.map((nameTitle) => {
                                return (
                                  <TableCell key={nameTitle.name} sx={{pt: 0, pb: 1}}></TableCell>
                                )
                              })}
                            </TableRow>) 
                          : null}
                          <TableRow
                            key={index}
                          >
                            <TableCell>{row.rowIndex}</TableCell>
                            {exampleColumnNamesAndTitles?.map((nameTitle) => {
                              return (
                                <TableCell key={nameTitle.name}>{row.record[exampleColumnIndexes[nameTitle.name]]}</TableCell>    
                              )
                            })}
                          </TableRow>
                        </>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )
          ) : null}
          {importButton}
          {!loading && validationErrors?.length ? (
            (
              <TableContainer component={Box} sx={{ boxShadow: 1, borderRadius: 1 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Row</TableCell>
                      {errorColumnNamesAndTitles?.map((nameTitle) => {
                        return (
                          <TableCell key={nameTitle.name}>{nameTitle.title}</TableCell>    
                        )
                      })}
                      <TableCell>Errors</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {validationErrors?.map((row, index) => {
                      return (
                        <TableRow
                          key={index}
                          sx={{
                            cursor: row.errors ? 'pointer' : 'default',
                            '&:hover': { backgroundColor: row.errors ? 'action.hover' : 'inherit' },
                          }}
                        >
                          <TableCell sx={{ color: 'error.main' }}>{row.rowIndex}</TableCell>
                          {errorColumnNamesAndTitles?.map((nameTitle) => {
                            return (
                              <TableCell key={nameTitle.name}>{row.csvRow[errorColumnIndexes[nameTitle.name]]}</TableCell>    
                            )
                          })}
                          <TableCell>
                            {row.errors?.map((error) => {
                              return (
                                <Chip
                                  key={error.instancePath}
                                  label={formatErrorMessage(error)}
                                  color={'error'}
                                  sx={{ color: 'white', fontWeight: 'bold', marginRight: 1 }}
                                />
                              )
                            })}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )
          ) : null}

          {/* buttons below are only show if there's validation errors otherwise will duplicate it with the top one */}
          {validationErrors?.length ? importButton : null}

          <ErrorDetailsModal
            open={modalOpen}
            onClose={handleCloseModal}
            errors={selectedRowErrors}
          />
        </Box>
      </CardContent>
    </Card>
    </>
  );
};

export default ImportPreview;
